/**
 * Circuit Breaker Pattern Implementation
 * 
 * Prevents cascading failures by "opening" the circuit when too many errors occur
 * Automatically recovers by periodically testing if the service is healthy again
 * 
 * States:
 * - CLOSED: Normal operation, requests pass through
 * - OPEN: Too many failures, requests fail immediately
 * - HALF_OPEN: Testing if service recovered, limited requests allowed
 */

const CircuitState = {
  CLOSED: 'CLOSED',
  OPEN: 'OPEN',
  HALF_OPEN: 'HALF_OPEN'
};

class CircuitBreaker {
  constructor(options = {}) {
    this.name = options.name || 'default';
    this.failureThreshold = options.failureThreshold || 5; // Failures before opening
    this.successThreshold = options.successThreshold || 2; // Successes needed to close
    this.timeout = options.timeout || 60000; // Time before trying again (1 minute)
    this.monitorInterval = options.monitorInterval || 10000; // Check interval
    
    this.state = CircuitState.CLOSED;
    this.failureCount = 0;
    this.successCount = 0;
    this.nextAttempt = Date.now();
    this.lastError = null;
    
    // Statistics
    this.stats = {
      totalRequests: 0,
      successfulRequests: 0,
      failedRequests: 0,
      rejectedRequests: 0,
      stateChanges: []
    };
  }

  /**
   * Execute function with circuit breaker protection
   * @param {Function} fn - Async function to execute
   * @returns {Promise} Result of function or rejection if circuit is open
   */
  async execute(fn) {
    this.stats.totalRequests++;

    // Check if circuit is open
    if (this.state === CircuitState.OPEN) {
      // Check if it's time to try again
      if (Date.now() < this.nextAttempt) {
        this.stats.rejectedRequests++;
        throw new Error(
          `Circuit breaker is OPEN for ${this.name}. ` +
          `Try again in ${Math.ceil((this.nextAttempt - Date.now()) / 1000)}s. ` +
          `Last error: ${this.lastError?.message || 'Unknown'}`
        );
      }

      // Move to half-open state
      this.setState(CircuitState.HALF_OPEN);
    }

    // Execute the function
    try {
      const result = await fn();
      this.onSuccess();
      return result;
    } catch (error) {
      this.onFailure(error);
      throw error;
    }
  }

  /**
   * Handle successful request
   */
  onSuccess() {
    this.stats.successfulRequests++;
    this.failureCount = 0;
    this.lastError = null;

    if (this.state === CircuitState.HALF_OPEN) {
      this.successCount++;
      
      // If we've had enough successes, close the circuit
      if (this.successCount >= this.successThreshold) {
        this.setState(CircuitState.CLOSED);
        this.successCount = 0;
      }
    }
  }

  /**
   * Handle failed request
   */
  onFailure(error) {
    this.stats.failedRequests++;
    this.failureCount++;
    this.lastError = error;
    this.successCount = 0;

    // Check if we should open the circuit
    if (this.failureCount >= this.failureThreshold) {
      this.setState(CircuitState.OPEN);
      this.nextAttempt = Date.now() + this.timeout;
    }
  }

  /**
   * Change circuit state
   */
  setState(newState) {
    if (this.state !== newState) {
      console.log(`🔌 Circuit Breaker [${this.name}]: ${this.state} → ${newState}`);
      
      this.stats.stateChanges.push({
        from: this.state,
        to: newState,
        timestamp: Date.now()
      });

      this.state = newState;

      // Reset counters on state change
      if (newState === CircuitState.CLOSED) {
        this.failureCount = 0;
        this.successCount = 0;
      }
    }
  }

  /**
   * Manually reset circuit breaker
   */
  reset() {
    console.log(`🔄 Manually resetting circuit breaker: ${this.name}`);
    this.setState(CircuitState.CLOSED);
    this.failureCount = 0;
    this.successCount = 0;
    this.nextAttempt = Date.now();
    this.lastError = null;
  }

  /**
   * Get current status
   */
  getStatus() {
    return {
      name: this.name,
      state: this.state,
      failureCount: this.failureCount,
      successCount: this.successCount,
      nextAttempt: this.nextAttempt,
      lastError: this.lastError?.message || null,
      stats: {
        ...this.stats,
        successRate: this.stats.totalRequests > 0
          ? ((this.stats.successfulRequests / this.stats.totalRequests) * 100).toFixed(2)
          : 100,
        rejectionRate: this.stats.totalRequests > 0
          ? ((this.stats.rejectedRequests / this.stats.totalRequests) * 100).toFixed(2)
          : 0
      }
    };
  }

  /**
   * Check if circuit is healthy
   */
  isHealthy() {
    return this.state === CircuitState.CLOSED;
  }
}

/**
 * Circuit Breaker Manager
 * Manages multiple circuit breakers for different services
 */
class CircuitBreakerManager {
  constructor() {
    this.breakers = new Map();
  }

  /**
   * Get or create circuit breaker
   */
  getBreaker(name, options = {}) {
    if (!this.breakers.has(name)) {
      this.breakers.set(name, new CircuitBreaker({ name, ...options }));
    }
    return this.breakers.get(name);
  }

  /**
   * Execute with circuit breaker
   */
  async execute(name, fn, options = {}) {
    const breaker = this.getBreaker(name, options);
    return breaker.execute(fn);
  }

  /**
   * Get status of all circuit breakers
   */
  getAllStatus() {
    const statuses = {};
    for (const [name, breaker] of this.breakers.entries()) {
      statuses[name] = breaker.getStatus();
    }
    return statuses;
  }

  /**
   * Get health check for all breakers
   */
  getHealthCheck() {
    const health = {
      healthy: true,
      breakers: {}
    };

    for (const [name, breaker] of this.breakers.entries()) {
      const status = breaker.getStatus();
      health.breakers[name] = {
        state: status.state,
        healthy: breaker.isHealthy()
      };

      if (!breaker.isHealthy()) {
        health.healthy = false;
      }
    }

    return health;
  }

  /**
   * Reset all circuit breakers
   */
  resetAll() {
    for (const breaker of this.breakers.values()) {
      breaker.reset();
    }
  }

  /**
   * Reset specific circuit breaker
   */
  reset(name) {
    const breaker = this.breakers.get(name);
    if (breaker) {
      breaker.reset();
    }
  }
}

// Create singleton manager
const circuitBreakerManager = new CircuitBreakerManager();

export default circuitBreakerManager;
export { CircuitBreaker, CircuitState };

/**
 * Pre-configured circuit breakers for common services
 */

// RPC calls (Solana blockchain)
export async function executeWithRpcCircuitBreaker(fn) {
  return circuitBreakerManager.execute('solana-rpc', fn, {
    failureThreshold: 5,
    successThreshold: 2,
    timeout: 30000 // 30 seconds
  });
}

// Supabase API calls
export async function executeWithSupabaseCircuitBreaker(fn) {
  return circuitBreakerManager.execute('supabase', fn, {
    failureThreshold: 10,
    successThreshold: 3,
    timeout: 60000 // 1 minute
  });
}

// External price API calls
export async function executeWithPriceApiCircuitBreaker(fn) {
  return circuitBreakerManager.execute('price-api', fn, {
    failureThreshold: 3,
    successThreshold: 2,
    timeout: 120000 // 2 minutes
  });
}

// Helius API calls
export async function executeWithHeliusCircuitBreaker(fn) {
  return circuitBreakerManager.execute('helius', fn, {
    failureThreshold: 5,
    successThreshold: 2,
    timeout: 45000 // 45 seconds
  });
}

/**
 * Get health status of all services
 */
export function getServicesHealth() {
  return circuitBreakerManager.getHealthCheck();
}

/**
 * Get detailed status of all circuit breakers
 */
export function getAllCircuitBreakerStatus() {
  return circuitBreakerManager.getAllStatus();
}

/**
 * Reset all circuit breakers
 */
export function resetAllCircuitBreakers() {
  circuitBreakerManager.resetAll();
}

/**
 * Reset specific circuit breaker
 */
export function resetCircuitBreaker(name) {
  circuitBreakerManager.reset(name);
}

